/**
 * Deposit Exit Intent Modal Manager
 * 
 * Handles the exit intent modal that appears when users try to close
 * the deposit modal without completing a deposit.
 * 
 * @package betbeast
 */

import { getHomeRedirectUrl } from '../account-redirect-utils.js';

export class DepositExitIntentModal {
    constructor(modalId = 'deposit-modal-exit-intent') {
        this.modal = document.getElementById(modalId);
        this.isOpen = false;
        this.hasShownExitIntent = false;
        
        if (!this.modal) {
            console.warn(`Exit intent modal with ID "${modalId}" not found`);
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize modal event listeners
     */
    init() {
        // Close button handler (Close link only — overlay click and Escape do not dismiss)
        const closeButtons = this.modal.querySelectorAll('[data-exit-intent-close]');
        closeButtons.forEach(button => {
            button.addEventListener('click', () => this.close());
        });

        // Back to deposit button
        const backButton = this.modal.querySelector('[data-back-to-deposit]');
        if (backButton) {
            backButton.addEventListener('click', () => this.handleBackToDeposit());
        }
    }
    
    /**
     * Open the exit intent modal
     */
    open() {
        if (!this.modal) return;

        // Mirror the bonus currently selected in the deposit modal
        this.syncSelectedBonus();

        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        this.isOpen = true;
        this.hasShownExitIntent = true;
        
        // Focus management for accessibility
        const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    /**
     * Sync the exit intent bonus card with the bonus selected in the deposit modal.
     * Shows the no-bonus version when no bonus is selected (or none are available).
     */
    syncSelectedBonus() {
        const bonusBox = this.modal.querySelector('[data-exit-bonus-box]');
        const bonusText = this.modal.querySelector('[data-exit-bonus-text]');
        const noBonusText = this.modal.querySelector('[data-exit-no-bonus-text]');

        // Mirror the deposit modal's selected-bonus card. Its `data-bonus-id` is set when a
        // real bonus is selected and removed for the "I don't want a bonus" option, so its
        // presence is the signal that a bonus is currently selected.
        const depositModal = document.getElementById('wallet-deposit-modal');
        const selectedCard = depositModal
            ? depositModal.querySelector('.wallet-deposit-selected-bonus[data-bonus-id]')
            : null;

        if (selectedCard) {
            // Category is the bonus's display name (always set); the title is its headline
            // (may be empty, e.g. for "Free spins" bonuses).
            const category = this.textOf(selectedCard, '.wallet-deposit-selected-bonus-category');
            const headline = this.textOf(selectedCard, '.wallet-deposit-selected-bonus-title');
            const bonusName = headline || category;

            this.setText('[data-exit-bonus-title]', category);
            this.setText('[data-exit-bonus-header]', headline);
            this.setText('[data-exit-bonus-header-text]', bonusName);

            this.syncBonusImages(selectedCard);

            if (bonusBox) bonusBox.style.display = '';
            if (bonusText) bonusText.style.display = '';
            if (noBonusText) noBonusText.style.display = 'none';
        } else {
            // No bonus selected / none available — show the no-bonus version
            if (bonusBox) bonusBox.style.display = 'none';
            if (bonusText) bonusText.style.display = 'none';
            if (noBonusText) noBonusText.style.display = '';
        }
    }

    /**
     * Rebuild the exit intent bonus images by cloning them from the selected-bonus card.
     * @param {HTMLElement} selectedCard - The .wallet-deposit-selected-bonus element
     */
    syncBonusImages(selectedCard) {
        const target = this.modal.querySelector('[data-exit-bonus-images]');
        const source = selectedCard.querySelector('.wallet-deposit-selected-bonus-images');
        if (!target || !source) return;

        target.innerHTML = '';

        const imageMap = [
            ['--background', 'deposit-modal-exit__bonus-image--background deposit-modal-exit__bonus-image--background-exit-intent'],
            ['--effect', 'deposit-modal-exit__bonus-image--effect deposit-modal-exit__bonus-image--effect-exit-intent'],
            ['--foreground', 'deposit-modal-exit__bonus-image--foreground deposit-modal-exit__bonus-image--foreground-exit-intent'],
        ];

        imageMap.forEach(([modifier, exitClasses]) => {
            const sourceImg = source.querySelector('.wallet-deposit-selected-bonus-image' + modifier);
            if (sourceImg) {
                const clone = sourceImg.cloneNode(true);
                clone.className = 'deposit-modal-exit__bonus-image ' + exitClasses;
                target.appendChild(clone);
            }
        });
    }

    /**
     * Read trimmed text content of a descendant, or '' if not found.
     */
    textOf(root, selector) {
        const el = root.querySelector(selector);
        return el ? el.textContent.trim() : '';
    }

    /**
     * Set text content on the exit intent element matching the selector, if present.
     */
    setText(selector, value) {
        const el = this.modal.querySelector(selector);
        if (el) el.textContent = value;
    }

    /**
     * Close the exit intent modal
     * @param {boolean} allowReopenDepositModal - Whether to allow reopening the main deposit modal
     */
    close(allowReopenDepositModal = false) {
        if (!this.modal) return;
        
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        this.isOpen = false;
        
        // Dispatch event so deposit modal knows exit intent was dismissed
        if (!allowReopenDepositModal) {
            const event = new CustomEvent('exitIntentDismissed');
            document.dispatchEvent(event);
            
            // Redirect to homepage
            window.location.href = getHomeRedirectUrl();
        }
    }
    
    /**
     * Handle back to deposit button click
     */
    handleBackToDeposit() {
        this.close(true);
        
        // Dispatch event to reopen deposit modal
        const event = new CustomEvent('returnToDeposit');
        document.dispatchEvent(event);
    }
    
    /**
     * Reset the exit intent state (useful for new deposit attempts)
     */
    reset() {
        this.hasShownExitIntent = false;
    }
    
    /**
     * Check if exit intent has been shown
     */
    hasShown() {
        return this.hasShownExitIntent;
    }
}
